import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { ro } from 'date-fns/locale'
import CNPInput from './CNPInput'
import apiClient from '../../services/api'
import toast from 'react-hot-toast'

interface QuickAddPatientFormProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  tenantId: string
  institutionId: string
}

interface PatientFormData {
  first_name: string
  last_name: string
  cnp: string
  phone: string
  dob: Date | null
  sex: string
  email: string
  is_foreign: boolean
  country_of_origin: string
  id_document_type: string
  id_document_series: string
  id_document_number: string
  county: string
  city: string
  street: string
  street_number: string
}

const QuickAddPatientForm: React.FC<QuickAddPatientFormProps> = ({
  open,
  onClose,
  onSuccess,
  tenantId,
  institutionId,
}) => {
  const [formData, setFormData] = useState<PatientFormData>({
    first_name: '',
    last_name: '',
    cnp: '',
    phone: '',
    dob: null,
    sex: '',
    email: '',
    is_foreign: false,
    country_of_origin: '',
    id_document_type: '',
    id_document_series: '',
    id_document_number: '',
    county: '',
    city: '',
    street: '',
    street_number: '',
  })

  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [cnpAutoFilled, setCnpAutoFilled] = useState(false)

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setFormData({
        first_name: '',
        last_name: '',
        cnp: '',
        phone: '',
        dob: null,
        sex: '',
        email: '',
        is_foreign: false,
        country_of_origin: '',
        id_document_type: '',
        id_document_series: '',
        id_document_number: '',
        county: '',
        city: '',
        street: '',
        street_number: '',
      })
      setErrors({})
      setCnpAutoFilled(false)
    }
  }, [open])

  const handleCNPChange = (cnp: string, extractedData?: { dob: string; sex: string }) => {
    setFormData((prev) => ({ ...prev, cnp }))

    // Auto-fill DOB and sex if extracted from CNP
    if (extractedData) {
      setCnpAutoFilled(true)
      setFormData((prev) => ({
        ...prev,
        dob: new Date(extractedData.dob),
        sex: extractedData.sex,
      }))
    }
  }

  const handleForeignCheckboxChange = (checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      is_foreign: checked,
      cnp: '', // Clear CNP when switching
      country_of_origin: checked ? '' : '',
    }))
    setCnpAutoFilled(false)
  }

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'Prenumele este obligatoriu'
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Numele este obligatoriu'
    }

    // Phone is optional - no validation

    // If no CNP, DOB and sex are required
    if (!formData.cnp) {
      if (!formData.dob) {
        newErrors.dob = 'Data nașterii este obligatorie când CNP lipsește'
      }
      if (!formData.sex) {
        newErrors.sex = 'Sexul este obligatoriu când CNP lipsește'
      }
    }

    // If foreign patient, require country and ID document
    if (formData.is_foreign) {
      if (!formData.country_of_origin.trim()) {
        newErrors.country_of_origin = 'Țara de origine este obligatorie pentru pacienți străini'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    // Validate tenant and institution IDs
    if (!tenantId || tenantId.trim().length === 0) {
      toast.error('Tenant ID invalid - vă rugăm reîncărcați pagina')
      return
    }
    
    if (!institutionId || institutionId.trim().length === 0) {
      toast.error('Institution ID invalid - vă rugăm reîncărcați pagina')
      return
    }
    
    if (!validate()) {
      toast.error('Vă rugăm completați toate câmpurile obligatorii')
      return
    }

    setLoading(true)

    try {
      const payload = {
        tenant_id: tenantId,
        institution_id: institutionId,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        cnp: formData.cnp || null,
        phone: formData.phone.trim() || null,
        dob: formData.dob ? formData.dob.toISOString().split('T')[0] : null,
        sex: formData.sex || null,
        email: formData.email.trim() || null,
        country_of_origin: formData.is_foreign ? formData.country_of_origin.trim() : null,
        id_document_type: formData.is_foreign ? formData.id_document_type.trim() || null : null,
        id_document_series: formData.is_foreign ? formData.id_document_series.trim() || null : null,
        id_document_number: formData.is_foreign ? formData.id_document_number.trim() || null : null,
        county: formData.county.trim() || null,
        city: formData.city.trim() || null,
        street: formData.street.trim() || null,
        street_number: formData.street_number.trim() || null,
      }

      await apiClient.post('/patients', payload)

      toast.success('Pacient adăugat cu succes!')
      onSuccess()
      onClose()
    } catch (error: any) {
      console.error('Error creating patient:', error)
      const errorMessage = error.response?.data?.detail || 'Eroare la adăugarea pacientului'
      toast.error(errorMessage)

      // Set field-specific errors if available
      if (error.response?.data?.validation_errors) {
        setErrors(error.response.data.validation_errors)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog 
      open={open} 
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      disableEscapeKeyDown
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          backgroundImage: 'none',
          '& .MuiInputLabel-root': {
            color: 'text.secondary',
            fontSize: '0.85rem',
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: 'primary.main',
          },
          '& .MuiOutlinedInput-root': {
            fontSize: '0.875rem',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'divider',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'primary.main',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'primary.main',
            },
          },
          '& .MuiSelect-select': {
            color: 'text.primary',
          },
          '& .MuiMenuItem-root': {
            fontSize: '0.875rem',
          },
        }
      }}
    >
      <DialogTitle sx={{ 
        py: 1.5, 
        px: 2,
        fontSize: '1rem',
        fontWeight: 600,
        borderBottom: '1px solid',
        borderColor: 'divider',
        color: 'text.primary'
      }}>
        Adaugă Pacient Nou
      </DialogTitle>
      <DialogContent sx={{ p: 2 }}>
        <Box sx={{ pt: 1 }}>
          <Grid container spacing={1.5}>
            {/* Basic Info */}
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ 
                fontWeight: 600, 
                color: 'primary.main',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Date Identificare
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                required
                label="Prenume"
                value={formData.first_name}
                onChange={(e) => setFormData((prev) => ({ ...prev, first_name: e.target.value }))}
                error={!!errors.first_name}
                helperText={errors.first_name}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                required
                label="Nume"
                value={formData.last_name}
                onChange={(e) => setFormData((prev) => ({ ...prev, last_name: e.target.value }))}
                error={!!errors.last_name}
                helperText={errors.last_name}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                sx={{ 
                  ml: 0,
                  '& .MuiFormControlLabel-label': { 
                    fontSize: '0.85rem',
                    color: 'text.primary'
                  }
                }}
                control={
                  <Checkbox
                    checked={formData.is_foreign}
                    onChange={(e) => handleForeignCheckboxChange(e.target.checked)}
                    disabled={loading}
                    size="small"
                  />
                }
                label="Pacient fără CNP român (străin sau copil)"
              />
            </Grid>

            {!formData.is_foreign && (
              <Grid item xs={12}>
                <CNPInput
                  value={formData.cnp}
                  onChange={handleCNPChange}
                  disabled={loading}
                  error={errors.cnp}
                  required={!formData.is_foreign}
                />
              </Grid>
            )}

            {formData.is_foreign && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    required
                    label="Țara de origine"
                    value={formData.country_of_origin}
                    onChange={(e) => setFormData((prev) => ({ ...prev, country_of_origin: e.target.value }))}
                    error={!!errors.country_of_origin}
                    helperText={errors.country_of_origin}
                    disabled={loading}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tip Document ID</InputLabel>
                    <Select
                      value={formData.id_document_type}
                      onChange={(e) => setFormData((prev) => ({ ...prev, id_document_type: e.target.value }))}
                      label="Tip Document ID"
                      disabled={loading}
                    >
                      <MenuItem value="passport">Pașaport</MenuItem>
                      <MenuItem value="id_card">Carte de Identitate</MenuItem>
                      <MenuItem value="residence_permit">Permis de Ședere</MenuItem>
                      <MenuItem value="other">Altul</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Serie Document"
                    value={formData.id_document_series}
                    onChange={(e) => setFormData((prev) => ({ ...prev, id_document_series: e.target.value }))}
                    disabled={loading}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Număr Document"
                    value={formData.id_document_number}
                    onChange={(e) => setFormData((prev) => ({ ...prev, id_document_number: e.target.value }))}
                    disabled={loading}
                  />
                </Grid>
              </>
            )}

            {(formData.is_foreign || !cnpAutoFilled) && (
              <>
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ro}>
                    <DatePicker
                      label="Data Nașterii"
                      value={formData.dob}
                      onChange={(date) => setFormData((prev) => ({ ...prev, dob: date }))}
                      disabled={loading || cnpAutoFilled}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          required: formData.is_foreign || !formData.cnp,
                          error: !!errors.dob,
                          helperText: errors.dob || (cnpAutoFilled ? 'Auto-completat din CNP' : ''),
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small" required={formData.is_foreign || !formData.cnp} error={!!errors.sex}>
                    <InputLabel>Sex</InputLabel>
                    <Select
                      value={formData.sex}
                      onChange={(e) => setFormData((prev) => ({ ...prev, sex: e.target.value }))}
                      label="Sex"
                      disabled={loading || cnpAutoFilled}
                    >
                      <MenuItem value="M">Masculin</MenuItem>
                      <MenuItem value="F">Feminin</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            {cnpAutoFilled && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ 
                  py: 0.5,
                  '& .MuiAlert-message': {
                    fontSize: '0.8rem'
                  }
                }}>
                  Data nașterii și sexul au fost completate automat din CNP
                </Alert>
              </Grid>
            )}

            {/* Contact Info */}
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ 
                fontWeight: 600, 
                color: 'primary.main',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mt: 1,
                display: 'block'
              }}>
                Date Contact
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Telefon"
                value={formData.phone}
                onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                error={!!errors.phone}
                helperText={errors.phone}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                error={!!errors.email}
                helperText={errors.email}
                disabled={loading}
              />
            </Grid>

            {/* Address */}
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ 
                fontWeight: 600, 
                color: 'primary.main',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mt: 1,
                display: 'block'
              }}>
                Adresă (opțional)
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Județ"
                value={formData.county}
                onChange={(e) => setFormData((prev) => ({ ...prev, county: e.target.value }))}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Oraș"
                value={formData.city}
                onChange={(e) => setFormData((prev) => ({ ...prev, city: e.target.value }))}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={9}>
              <TextField
                fullWidth
                size="small"
                label="Stradă"
                value={formData.street}
                onChange={(e) => setFormData((prev) => ({ ...prev, street: e.target.value }))}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Număr"
                value={formData.street_number}
                onChange={(e) => setFormData((prev) => ({ ...prev, street_number: e.target.value }))}
                disabled={loading}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ 
        px: 2, 
        py: 1.5,
        borderTop: '1px solid',
        borderColor: 'divider',
        gap: 1
      }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          size="small"
          sx={{ 
            fontSize: '0.8rem',
            color: 'text.secondary',
            '&:hover': {
              color: 'text.primary'
            }
          }}
        >
          Anulează
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          size="small"
          startIcon={loading ? <CircularProgress size={16} /> : null}
          sx={{ fontSize: '0.8rem' }}
        >
          {loading ? 'Se salvează...' : 'Adaugă Pacient'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default QuickAddPatientForm

